import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextProps {
    theme: ThemeMode;
    setTheme: (next: ThemeMode) => void;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

// Persisted user preference. When absent we default to 'dark' so existing
// users keep the current authenticated-shell look; only users who explicitly
// switch will land on light mode.
const THEME_STORAGE_KEY = 'devanza:theme';

const readInitialTheme = (): ThemeMode => {
    try {
        const v = window.localStorage.getItem(THEME_STORAGE_KEY);
        if (v === 'light' || v === 'dark') return v;
    } catch {
        /* noop — fall through to default */
    }
    return 'dark';
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [theme, setThemeState] = useState<ThemeMode>(readInitialTheme);

    // Drive the actual presentation by toggling `body.dark-theme`. The class
    // hook is what the `body.dark-theme` semantic overrides in
    // `src/styles/tokens.css` target (legacy `--color-*` names alias them from
    // global.css's `body {}` block); the default `:root` tokens already define
    // the light theme so removing the class IS the light theme.
    useEffect(() => {
        document.body.classList.toggle('dark-theme', theme === 'dark');
    }, [theme]);

    const setTheme = useCallback((next: ThemeMode) => {
        setThemeState(next);
        try {
            window.localStorage.setItem(THEME_STORAGE_KEY, next);
        } catch {
            /* noop — preference simply won't persist */
        }
    }, []);

    const toggleTheme = useCallback(() => {
        setTheme(theme === 'dark' ? 'light' : 'dark');
    }, [theme, setTheme]);

    return (
        <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
